import { combineEpics, Epic } from 'redux-observable';
import { EMPTY, from, of } from 'rxjs';
import {
  switchMap,
  map,
  mergeMap,
  startWith,
  catchError,
  filter
} from 'rxjs/operators';
import { isOfType } from 'typesafe-actions';
import { Position, Toaster, Intent } from '@blueprintjs/core';

import {
  BlockActions,
  BlockActionTypes,
  createdBlock,
  creatingBlock,
  creatingBlockFailed,
  createdBlocks,
  creatingBlocks,
  creatingBlocksFailed,
  updatedBlock,
  updatingBlock,
  updatingBlockFailed,
  updatingBlocks,
  updatedBlocks,
  updatingBlocksFailed,
  deletedBlock,
  deletingBlock,
  deletingBlockFailed,
  loadingPreview,
  loadedPreview,
  loadingPreviewFailed,
} from '../actions/blockActions';
import { IState } from '../reducers';
import {
  createBlock,
  createBlocks,
  updateBlock,
  deleteBlock,
  previewBlock,
  updateBlocks,
} from '../../services/blockApi';

export const blockToaster = Toaster.create({
  className: 'block-toaster',
  position: Position.TOP
});

const createBlockEpic: Epic<BlockActions, BlockActions, IState, any> = (
  action$: any,
  state$: any,
  { dispatch }: any
) =>
  action$.pipe(
    filter(isOfType(BlockActionTypes.CREATE_BLOCK)),
    mergeMap((action: any) =>
      from(createBlock(action.payload)).pipe(
        map((response: any) => createdBlock(response.result)),
        startWith(creatingBlock()),
        catchError((error: any) => {
          const message = error.response?.data?.message || "";

          blockToaster.show({
            message: `An error occurred while creating block: ${message}.`,
            intent: Intent.DANGER,
          });

          return of(creatingBlockFailed());
        })
      )
    )
  );

const createBlocksEpic: Epic<BlockActions, BlockActions, IState, any> = (
  action$: any,
  state$: any,
  { dispatch }: any
) =>
  action$.pipe(
    filter(isOfType(BlockActionTypes.CREATE_BLOCKS)),
    switchMap((action: any) =>
      from(createBlocks(action.payload)).pipe(
        map((response: any) => createdBlocks(response)),
        startWith(creatingBlocks()),
        catchError((error: any) => {
          const message = error.response?.data?.message || "";

          blockToaster.show({
            message: `An error occurred while creating blocks: ${message}.`,
            intent: Intent.DANGER,
          });

          return of(creatingBlocksFailed());
        })
      )
    )
  );

const updateBlockEpic: Epic<BlockActions, BlockActions, IState, any> = (
  action$: any,
  state$: any,
  { dispatch }: any
) =>
  action$.pipe(
    filter(isOfType(BlockActionTypes.UPDATE_BLOCK)),
    switchMap((action: any) =>
      from(updateBlock(action.payload.uid as string, action.payload.block)).pipe(
        map((response: any) => updatedBlock({
          ...response.result,
          uid: action.payload.uid
        }, Object.keys(action.payload.block))),
        startWith(updatingBlock()),
        catchError((error: any) => {
          const message = error.response?.data?.message || "";

          blockToaster.show({
            message: `An error occurred while updating block: ${message}.`,
            intent: Intent.DANGER
          });

          return of(updatingBlockFailed());
        })
      )
    )
  );

const updateBlocksEpic: Epic<BlockActions, BlockActions, IState, any> = (
  action$: any,
  state$: any,
  { dispatch }: any
) =>
  action$.pipe(
    filter(isOfType(BlockActionTypes.UPDATE_BLOCKS)),
    switchMap((action: any) =>
      from(updateBlocks(action.payload.blocks)).pipe(
        map((response: any) => updatedBlocks(response.blocks)),
        startWith(updatingBlocks()),
        catchError((error: any) => {
          const message = error.response?.data?.message || "";

          blockToaster.show({
            message: `An error occurred while updating blocks: ${message}.`,
            intent: Intent.DANGER,
          });

          return of(updatingBlocksFailed());
        })
      )
    )
  );

const deleteBlockEpic: Epic<BlockActions, BlockActions, IState, any> = (
  action$: any,
  state$: any,
  { dispatch }: any
) =>
  action$.pipe(
    filter(isOfType(BlockActionTypes.DELETE_BLOCK)),
    switchMap((action: any) => {
      if (action.payload.softDelete) {
        /*
         * Delete it only from redux, but not from the Backend
         */
        dispatch(deletedBlock(action.payload.uid));
        return EMPTY;
      }
      /*
       * TODO: find a solution if blocks are removed by `batchId`; not urgent as the code below
       *   it's not reached.
       */

      return from(deleteBlock(action.payload.uid)).pipe(
        map((response: any) => {
          return deletedBlock(action.payload.uid);
        }),
        startWith(deletingBlock()),
        catchError((error: any) => {
          const message = error.response?.data?.message || "";

          blockToaster.show({
            message: `An error occurred while deleting report: ${message}.`,
            intent: Intent.DANGER
          });

          return of(deletingBlockFailed());
        })
      )
    })
  );

const previewBlockEpic: Epic<BlockActions, BlockActions, IState, any> = (
  action$: any,
  state$: any,
  { dispatch }: any
) => action$.pipe(
    filter(isOfType(BlockActionTypes.LOAD_PREVIEW)),
    switchMap((action: any) => {
      return from(
        previewBlock(action.payload.uid, action.payload.reportUid)).
      pipe(
          map((response: any) => loadedPreview(action.payload.uid, response)),
          startWith(loadingPreview()),
          catchError((error: any) => {
            const message = error.response?.data?.message || '';

            blockToaster.show({
              message: `Preview error: ${message}.`,
              intent: Intent.DANGER
            });

            return of(loadingPreviewFailed());
          }),
        )
    }),
  );

export default combineEpics(
  createBlockEpic,
  createBlocksEpic,
  updateBlockEpic,
  updateBlocksEpic,
  deleteBlockEpic,
  previewBlockEpic,
);
